import React, { useEffect, useState, useContext, useCallback } from 'react';
import { Typography, Button, IconButton, ImageListItem, ImageListItemBar } from '@common-ui/ui';
import { Brightness7, BrightnessAuto, Circle } from '@mui/icons-material';
import ImageThumbnail from './ImageThumbnail';
import SidebarDrawer from './SidebarDrawer';
import { ModalContext } from '../../../components/modals/Report';

function BatchIconState({ isBatch = false, isPrimary = false }) {
  if (isPrimary) {
    return <Brightness7 />;
  } else if (isBatch) {
    return <BrightnessAuto style={{ color: 'rgba(255,255,255,1)' }} />;
  } else {
    return <Circle style={{ color: 'rgba(255,255,255,0.5)' }} />;
  }
}

function BatchThumbnail({
  image = {},
  isPrimary = false,
  isBatch = false,
  onPrimary = null,
  onSecondary = null,
  disableOverlay = false,
}) {
  const handlePrimary = useCallback(() => {
    onPrimary?.();
  }, [onPrimary]);

  const handleCheck = useCallback(
    (e) => {
      e.stopPropagation();
      onSecondary?.();
    },
    [onSecondary],
  );

  if (Object.keys(image) < 1) return <></>;

  return (
    <ImageListItem
      onClick={() => handlePrimary?.()}
      id="carousel-image-preview"
      sx={isPrimary ? styles.imageSelected : styles.imageUnselected}
    >
      <div style={{ position: 'relative' }}>
        <ImageThumbnail image={image} style={styles.image} hidden={disableOverlay} />
        <ImageListItemBar
          position="top"
          actionPosition="left"
          hidden={disableOverlay}
          actionIcon={
            <IconButton color="primary" onClick={handleCheck}>
              <BatchIconState isBatch={isBatch} isPrimary={isPrimary} />
            </IconButton>
          }
        />
      </div>

      {/* Add your ImageListItemBar and other components here */}
    </ImageListItem>
  );
}

export default function Carousel({ order = [], images = {}, onChange = null }) {
  const [primaryImage, setPrimaryImage] = useState(null);
  const [batchImages, setBatchImages] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const { handleModalData = {} } = useContext(ModalContext);

  useEffect(() => {
    if (!primaryImage && order.length > 0) {
      setPrimaryImage(order[0]);
    }
  }, [order]);

  useEffect(() => {
    handleModalData(images);
  }, [Object.keys(images)]);

  useEffect(() => {
    const newSelectedImages = [primaryImage, ...batchImages];
    onChange?.(newSelectedImages);
  }, [primaryImage, batchImages]);

  const handlePrimaryImage = ({ id }) => {
    if (primaryImage !== id) {
      // change primary image
      setPrimaryImage(id);
    }
    // set primary as only image
    setBatchImages([]);
  };

  const handleBatchInclude = ({ id }) => {
    setBatchImages((prev) => {
      if (prev.includes(id)) {
        // Remove the ID from the array and remove any null positions
        const updatedData = prev.filter((item) => item !== id).filter(Boolean);
        return updatedData;
      } else {
        // Append the ID to the array
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    if (order.length > 0) {
      let nextBatchImages = [];
      for (const id of order) {
        nextBatchImages.push(id);
      }
      setBatchImages(nextBatchImages);
    }
  };

  const handleDrawerToggle = () => {
    setIsDrawerOpen((prev) => !prev);
  };

  return (
    <SidebarDrawer isOpen={isDrawerOpen} onChange={handleDrawerToggle} width={250} closeWidth={100}>
      <div style={styles.sidebar}>
        {/* SELECTED IMAGE COUNT */}
        <div style={{ ...styles.label, display: isDrawerOpen ? 'flex' : 'none' }}>
          <Button onClick={handleSelectAll}>Select All</Button>
          <Typography>{`${batchImages.length || 1}/${order.length} Selected`}</Typography>
        </div>
        <div style={styles.imagesList}>
          {/* IMAGE PREVIEW LIST */}
          {order.map((id, index) => (
            <BatchThumbnail
              key={`carousel-image-${index}-${id}`}
              image={images?.[id]}
              isPrimary={primaryImage === id}
              isBatch={batchImages.includes(id)}
              onPrimary={() => handlePrimaryImage({ index, id })}
              onSecondary={() => handleBatchInclude({ index, id })}
              disableOverlay={!isDrawerOpen}
            />
          ))}
        </div>
      </div>
    </SidebarDrawer>
  );
}

const styles = {
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    width: '250px',
    overflow: 'hidden',
    padding: '8px',
  },
  imagesList: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '8px',
    flex: '1 1 auto', // This makes the imagesList take up any remaining space
  },
  label: {
    whiteSpace: 'nowrap',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    color: 'white',
    padding: '5px 10px',
    border: '2px solid white',
    borderRadius: '10px', // Adjust the value to control the roundness
  },

  imageSelected: {
    border: 2,
    borderColor: 'primary.main',
    cursor: 'pointer',
  },
  imageUnselected: {
    border: 2,
    borderColor: 'transparent',
    cursor: 'pointer',
  },
  image: {
    width: '100%',
    height: 'auto',
  },
};
